import type { ComparisonPreset, ComparisonDates, TimeGranularity, ScaleGroup } from './types';

export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value);
}

export function formatCompactCurrency(value: number): string {
  if (Math.abs(value) >= 1000000) return `R$ ${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `R$ ${(value / 1000).toFixed(0)}K`;
  return `R$ ${value.toFixed(0)}`;
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('pt-BR').format(value);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR');
}

export function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

export function formatMonthKey(monthKey: string): string {
  const [year, month] = monthKey.split('-');
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${months[parseInt(month) - 1]}/${year}`;
}

export function formatDayKey(dayKey: string): string {
  // dayKey is YYYY-MM-DD
  const date = new Date(dayKey + 'T00:00:00');
  const weekdays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const weekday = weekdays[date.getDay()];
  return `${weekday} ${day}/${month}`;
}

export function formatDimensionValue(value: string, dimensionKey: string): string {
  if (dimensionKey === '_month') return formatMonthKey(value);
  if (dimensionKey === '_day') return formatDayKey(value);
  return value;
}

export function calculateComparisonDates(
  preset: ComparisonPreset,
  startDate: Date,
  endDate: Date
): ComparisonDates | null {
  const formatDateStr = (d: Date): string => d.toISOString().split('T')[0];
  const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

  switch (preset) {
    case 'none':
      return null;

    case 'previous_period': {
      const compEnd = new Date(startDate);
      compEnd.setDate(compEnd.getDate() - 1);
      const compStart = new Date(compEnd);
      compStart.setDate(compStart.getDate() - daysDiff);
      return {
        startDate: formatDateStr(startDate),
        endDate: formatDateStr(endDate),
        compareStartDate: formatDateStr(compStart),
        compareEndDate: formatDateStr(compEnd),
      };
    }

    case 'previous_year': {
      const compStart = new Date(startDate);
      compStart.setFullYear(compStart.getFullYear() - 1);
      const compEnd = new Date(endDate);
      compEnd.setFullYear(compEnd.getFullYear() - 1);
      return {
        startDate: formatDateStr(startDate),
        endDate: formatDateStr(endDate),
        compareStartDate: formatDateStr(compStart),
        compareEndDate: formatDateStr(compEnd),
      };
    }

    default:
      return null;
  }
}

/**
 * Smart granularity selection based on date range (fallback when no explicit granularity):
 * - <=14 days: daily (max 14 bars)
 * - <=60 days: weekly (max ~9 bars)
 * - >60 days: monthly (max ~24 bars for 2 years)
 */
export function getAutoGranularity(startDate: Date, endDate: Date): TimeGranularity {
  const diffDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 14) return 'daily';
  if (diffDays <= 60) return 'weekly';
  return 'monthly';
}

export function getQuarter(date: Date): number {
  return Math.floor(date.getMonth() / 3) + 1;
}

export function getPeriodKey(date: Date, granularity: TimeGranularity): string {
  switch (granularity) {
    case 'accumulated':
      return 'total'; // Single bucket for all data
    case 'yearly':
      return `${date.getFullYear()}`;
    case 'quarterly':
      return `${date.getFullYear()}-Q${getQuarter(date)}`;
    case 'monthly':
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    case 'weekly': {
      // Get Monday of the week (ISO week start)
      const weekStart = new Date(date);
      const day = weekStart.getDay();
      const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
      weekStart.setDate(diff);
      return weekStart.toISOString().split('T')[0];
    }
    case 'daily':
    default:
      return date.toISOString().split('T')[0];
  }
}

export function formatPeriodLabel(key: string, granularity: TimeGranularity): string {
  const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

  switch (granularity) {
    case 'accumulated':
      return 'Total';
    case 'yearly':
      return key; // Just the year
    case 'quarterly': {
      const [year, quarter] = key.split('-');
      return `${quarter}/${year.slice(2)}`; // Q1/24
    }
    case 'monthly': {
      const [year, month] = key.split('-');
      return `${monthNames[parseInt(month) - 1]}/${year.slice(2)}`;
    }
    case 'weekly': {
      const date = new Date(key + 'T00:00:00');
      return `${date.getDate()}/${date.getMonth() + 1}`;
    }
    case 'daily':
    default: {
      const date = new Date(key + 'T00:00:00');
      return `${date.getDate()}/${date.getMonth() + 1}`;
    }
  }
}

/**
 * Groups metrics by similar scale (order of magnitude).
 * Returns 1 group if all scales are similar, 2 groups if scales differ significantly.
 *
 * Algorithm:
 * 1. Calculate max value for each metric across all periods
 * 2. If ratio between largest and smallest max is > 10x, split into 2 groups
 * 3. Assign groups to left/right axis
 */
export function groupMetricsByScale(
  seriesList: Array<{
    key: string;
    label: string;
    color: string;
    values: Map<string, number>;
    isCurrency: boolean;
  }>
): ScaleGroup[] {
  if (seriesList.length === 0) return [];
  if (seriesList.length === 1) {
    const series = seriesList[0];
    const maxValue = Math.max(...Array.from(series.values.values()), 1);
    return [{
      axisPosition: 'left',
      metrics: [{
        ...series,
        maxValue,
      }],
      maxValue,
      minValue: 0,
    }];
  }

  // Calculate max value for each metric
  const metricsWithMax = seriesList.map(series => {
    const values = Array.from(series.values.values());
    const maxValue = Math.max(...values, 1);
    return { ...series, maxValue };
  });

  // Sort by max value descending
  const sorted = [...metricsWithMax].sort((a, b) => b.maxValue - a.maxValue);

  // Check if we need to split (ratio > 10x between largest and smallest)
  const largestMax = sorted[0].maxValue;
  const smallestMax = sorted[sorted.length - 1].maxValue;
  const ratio = largestMax / Math.max(smallestMax, 0.001);

  // If ratio is small enough, keep all on single axis
  if (ratio <= 10) {
    return [{
      axisPosition: 'left',
      metrics: metricsWithMax, // Keep original colors
      maxValue: largestMax,
      minValue: 0,
    }];
  }

  // Split into 2 groups based on order of magnitude
  // Find the best split point (largest gap in log scale)
  let bestSplitIndex = 1;
  let bestGapRatio = 0;

  for (let i = 1; i < sorted.length; i++) {
    const gapRatio = sorted[i - 1].maxValue / Math.max(sorted[i].maxValue, 0.001);
    if (gapRatio > bestGapRatio) {
      bestGapRatio = gapRatio;
      bestSplitIndex = i;
    }
  }

  // Create two groups - metrics keep their original colors
  const leftGroup = sorted.slice(0, bestSplitIndex);
  const rightGroup = sorted.slice(bestSplitIndex);

  const groups: ScaleGroup[] = [];

  if (leftGroup.length > 0) {
    const leftMax = Math.max(...leftGroup.map(m => m.maxValue));
    groups.push({
      axisPosition: 'left',
      metrics: leftGroup, // Keep original colors
      maxValue: leftMax,
      minValue: 0,
    });
  }

  if (rightGroup.length > 0) {
    const rightMax = Math.max(...rightGroup.map(m => m.maxValue));
    groups.push({
      axisPosition: 'right',
      metrics: rightGroup, // Keep original colors
      maxValue: rightMax,
      minValue: 0,
    });
  }

  return groups;
}
